import { UAEntityTypes } from '@unifyapps/defs/types/entities';
import type { InterfaceEntity } from '@unifyapps/defs/types/interface';
import {
  useFindDeployedEntityById,
  useSearchDeployedInterfacePageEmbeddedEntities,
} from '@unifyapps/network/generated/entity-rest-api/entity-rest-api';
import { useMemo } from 'react';
import { debugSSR } from '../../../../../../consts/debug';
import { useEnvVariables } from '../../../../../../hooks/useEnvVariables';
import { InterfaceClient } from '../../../../../stores/InterfaceStore';
import {
  DEPLOYED_ALLOWED_ENTITY_TYPES,
  EMBEDDED_ENTITIES_EXCLUDE_FIELDS,
} from '../../../../../utils/interfaceQueryFilters';
import {
  getAllPagesFromEmbedded,
  normalizeEmbeddedDependents,
} from '../../../../../utils/normalizeEmbeddedDependents';
import { useResolvePageId } from '../../../../useResolvePageId';
import { buildInterfacePageEmbeddedRequest } from '../../../buildInterfacePageEmbeddedRequest';
import getHost from '../../../standardEntities/data/useFetchInterfaceResources/host';
import type { UseFetchResourcesHookType } from '../../../types';
import useFetchCustomBlocksForPages from '../../../useFetchCustomBlocks/useFetchCustomBlocksForPages';
import { useGetAuthDeployedInterface } from '../useAuthDeployedInterface';
import { useFetchDeployedInterfacePageNamespacesForPages } from '../useFetchDeployedInterfaceNamespaces/useFetchDeployedInterfacePageNamespacesForPages';

const useFetchDeployedInterfaceResources: UseFetchResourcesHookType = ({
  interfaceId,
  interfaceComponent,
  client,
  mode,
  disableAuth = false,
}) => {
  /**
   * Below two queries are ran conditionally based on client prop
   * Now lets say in a parent component(eg: InterfacePageComponent.tsx) we want useFindDeployedEntityById
   * hence enabled is true for useFindDeployedEntityById and false for useGetAuthDeployedInterface
   *
   * Now lets say in an app there is a module being rendered which will use the useGetAuthDeployedInterface query
   * Hence in that case enabled is true for useGetAuthDeployedInterface and false for useFindDeployedEntityById
   * Now when useGetAuthDeployedInterface query is in flight as the module requested it, the same query mounted in parent component
   * will also have isLoading as true. Now because of this the parent component will render the loader and not the child component.
   * This will lead to state reset as the store providers will unmount and mount again.
   *
   * Hence, to avoid this, the effective isLoading should be only be true if that query is enabled.
   */

  const shouldQueryAuthDeployedInterface =
    Boolean(getHost()) && client === InterfaceClient.UA_MATRIX && !disableAuth;
  // TODO: need backend API support to exclude fields (e.g. outSchema) from auth deployed interface endpoint to reduce payload size
  const authEntityInterface = useGetAuthDeployedInterface<InterfaceEntity>({
    // UA_MATRIX is for matrix app, so there we fetch from auth/interface endpoint that uses domain
    query: {
      enabled: shouldQueryAuthDeployedInterface,
      retry: 3,
      staleTime: Infinity,
    },
  });
  const isLoadingAuthDeployedInterfaceQuery =
    authEntityInterface.isLoading && shouldQueryAuthDeployedInterface;

  const shouldQueryDeployedInterfaceById =
    (Boolean(interfaceId) && client === InterfaceClient.UA_PLATFORM) || disableAuth;
  // TODO: need backend API support to exclude fields (e.g. outSchema) from findDeployedEntityById to reduce payload size in non-builder mode
  const { data, isLoading } = useFindDeployedEntityById<InterfaceEntity>(
    UAEntityTypes.Interface,
    interfaceId,
    {
      query: {
        enabled: shouldQueryDeployedInterfaceById,
        retry: 3,
        staleTime: Infinity,
      },
    },
  );
  const isLoadingDeployedInterfaceByIdQuery = isLoading && shouldQueryDeployedInterfaceById;

  const interfaceRecord = shouldQueryAuthDeployedInterface
    ? authEntityInterface.data || data
    : data;
  const isLoadingDeployedInterfaceQuery =
    isLoadingAuthDeployedInterfaceQuery || isLoadingDeployedInterfaceByIdQuery;
  const errorInterfaceRecord = authEntityInterface.error;

  const { envVariables, isLoading: envVariablesLoading } = useEnvVariables();

  const { pageId: defaultPageId } = useResolvePageId({
    interfaceRecord,
    envVariables,
    isLoadingEnvVariables: envVariablesLoading,
  });

  // Resolve the page request: prefer slug -> id via the interface record's slug map (with
  // legacy slug-as-id and defaultPageId fallbacks). When all those fail but the record is
  // loaded, the helper falls back to a slug-query payload so the backend resolves by slug.
  const { request, enabled } = useMemo(
    () =>
      buildInterfacePageEmbeddedRequest({
        interfaceId,
        interfaceComponent,
        interfaceRecord,
        defaultPageId,
        excludeFields: EMBEDDED_ENTITIES_EXCLUDE_FIELDS,
        allowedEntityTypes: DEPLOYED_ALLOWED_ENTITY_TYPES,
      }),
    [interfaceId, interfaceComponent, interfaceRecord, defaultPageId],
  );

  const {
    data: embeddedResp,
    isLoading: isLoadingPage,
    error: errorPage,
  } = useSearchDeployedInterfacePageEmbeddedEntities(request, {
    query: {
      enabled,
      retry: 3,
      staleTime: Infinity,
    },
  });

  const { interfacePage, dataSources, embeddedPages, embeddedInterfaceRecords } = useMemo(
    () => normalizeEmbeddedDependents(embeddedResp, true),
    [embeddedResp],
  );

  // Fetch namespaces and custom blocks for the root page plus every nested page returned by
  // embedded-dependents. The multi-page hooks issue one cache entry per (page, locale) tuple
  // matching the single-page hook's query keys, so nested pages can read warm cache later.
  const allPages = useMemo(
    () => getAllPagesFromEmbedded(interfacePage, embeddedPages),
    [interfacePage, embeddedPages],
  );

  const {
    namespaces,
    isLoading: isLoadingInterfaceNamespaces,
    error: errorInterfaceNamespaces,
  } = useFetchDeployedInterfacePageNamespacesForPages({
    interfaceId,
    pages: allPages,
    interfaceRecord,
    mode,
  });

  const interfacePageNamespace = interfacePage?.id ? namespaces?.[interfacePage.id] : undefined;

  const {
    customBlocks,
    customBlocksSnapshots,
    isLoading: isLoadingCustomBlocks,
  } = useFetchCustomBlocksForPages(allPages);

  const _isLoading =
    isLoadingDeployedInterfaceQuery ||
    isLoadingPage ||
    isLoadingInterfaceNamespaces ||
    isLoadingCustomBlocks;

  const _error = errorInterfaceRecord || errorPage || errorInterfaceNamespaces;

  debugSSR(
    'useFetchDeployedInterfaceResources',
    {
      isLoading: _isLoading,
      isLoadingDeployedInterfaceQuery,
      isLoadingPage,
      isLoadingInterfaceNamespaces,
      isLoadingCustomBlocks,
    },
    {
      interfaceId: interfaceRecord?.id,
      pageId: interfacePage?.id,
    },
  );

  return {
    isLoading: _isLoading,
    error: _error,
    data: interfaceRecord,
    dataSources,
    interfacePage,
    interfacePageNamespace,
    namespaces,
    embeddedPages,
    embeddedInterfaceRecords,
    customBlocks,
    customBlocksSnapshots,
  };
};

export default useFetchDeployedInterfaceResources;
